import { useEffect, useSyncExternalStore } from 'react';
import { CompanyRequestError } from '@/company/api';
import { useCompany } from '@/company/context';
import { useWorkspace } from '../context';
import { projectsApi } from './api';
import { forgetProjectPlaces, rememberProjectPlace } from './place';
import type { ProjectDetail, ProjectSummary } from './types';

// One copy of the projects the person can open, shared by the sidebar, All projects, a
// project's home and its About panel, so a change shows in all of them at once.

interface ListState {
  projects: ProjectSummary[];
  loading: boolean;
  loaded: boolean;
  error: string;
}
interface DetailState {
  project: ProjectDetail | null;
  loading: boolean;
  error: string;
  /** Gone, or not shared with the reader. */
  missing: boolean;
}

const EMPTY_LIST: ListState = { projects: [], loading: false, loaded: false, error: '' };
let scope = '';
let list = EMPTY_LIST;
const details = new Map<string, DetailState>();
let listGeneration = 0;
const detailGenerations = new Map<string, number>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const message = (failure: unknown, fallback: string) =>
  failure instanceof Error && failure.message && !(failure instanceof CompanyRequestError)
    ? failure.message
    : fallback;

const newestFirst = (projects: ProjectSummary[]) =>
  [...projects].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id));

/** Another account or company starts from nothing, names included. */
function scopeProjects(next: string) {
  if (next === scope) return;
  scope = next;
  list = EMPTY_LIST;
  details.clear();
  forgetProjectPlaces();
  listGeneration += 1;
  detailGenerations.clear();
  emit();
}

/** Signing out or in as someone else: forget everything before the next account renders. */
export function forgetProjects() {
  // Names can be remembered before the store is ever scoped (a chat started from a project).
  forgetProjectPlaces();
  scopeProjects('');
}

function remember(project: ProjectSummary) {
  rememberProjectPlace({ id: project.id, name: project.name, tint: project.tint });
}

export async function refreshProjects() {
  const generation = ++listGeneration;
  const owner = scope;
  list = { ...list, loading: true, error: '' };
  emit();
  try {
    const projects = await projectsApi.list();
    if (generation !== listGeneration || owner !== scope) return;
    projects.forEach(remember);
    list = { projects, loading: false, loaded: true, error: '' };
  } catch (failure) {
    if (generation !== listGeneration || owner !== scope) return;
    list = {
      ...list,
      loading: false,
      loaded: true,
      error: message(failure, 'Projects could not be loaded.'),
    };
  }
  emit();
}

export async function refreshProject(id: string) {
  const generation = (detailGenerations.get(id) ?? 0) + 1;
  detailGenerations.set(id, generation);
  const owner = scope;
  const before = details.get(id);
  details.set(id, {
    project: before?.project ?? null,
    loading: true,
    error: '',
    missing: false,
  });
  emit();
  try {
    const project = await projectsApi.get(id);
    if (detailGenerations.get(id) !== generation || owner !== scope) return;
    putProject(project);
  } catch (failure) {
    if (detailGenerations.get(id) !== generation || owner !== scope) return;
    const missing = failure instanceof CompanyRequestError && failure.status === 404;
    details.set(id, {
      project: missing ? null : (before?.project ?? null),
      loading: false,
      error: missing ? '' : message(failure, 'This project could not be loaded.'),
      missing,
    });
    if (missing) list = { ...list, projects: list.projects.filter((p) => p.id !== id) };
    emit();
  }
}

/** A project the server just returned (an add, a share, a new project): every view shows it.
 * A read already on its way is older, so it can't overwrite this. */
export function putProject(project: ProjectDetail) {
  detailGenerations.set(project.id, (detailGenerations.get(project.id) ?? 0) + 1);
  remember(project);
  details.set(project.id, { project, loading: false, error: '', missing: false });
  const {
    brief: _brief,
    briefRevision: _revision,
    briefChangedBy: _by,
    briefChangedAt: _at,
    follows: _follows,
    readsFirst: _reads,
    items: _items,
    needsYou: _needs,
    ...summary
  } = project;
  list = {
    ...list,
    projects: newestFirst([...list.projects.filter((p) => p.id !== project.id), summary]),
  };
  emit();
  rereadInFlightList();
}

/** A project that was deleted, or is no longer shared with the reader. A view still on it
 * says so rather than waiting for it. */
export function dropProject(id: string) {
  detailGenerations.set(id, (detailGenerations.get(id) ?? 0) + 1);
  details.set(id, { project: null, loading: false, error: '', missing: true });
  list = { ...list, projects: list.projects.filter((p) => p.id !== id) };
  emit();
  rereadInFlightList();
}

/** A list read that started before a write is read again, so it can't undo the write. */
function rereadInFlightList() {
  if (list.loading) void refreshProjects();
}

/** Whose projects these are, and whether they may be read yet: not before the company is
 * there, so a pending invitation is accepted first (as the Agents flags wait). */
function useScope() {
  const ws = useWorkspace();
  const company = useCompany();
  return {
    key: `${ws.user?.id ?? ''}:${company.data?.company?.id ?? ''}`,
    ready: company.ready && Boolean(company.data?.company?.id),
  };
}

/** Whether the account and company are still the ones a request started in. */
export function projectScope() {
  const owner = scope;
  return () => owner === scope;
}

/** putProject for a request starting now: ignored if the account or company changed since. */
export function projectWriter() {
  const inScope = projectScope();
  return (project: ProjectDetail) => {
    if (inScope()) putProject(project);
  };
}

/** The projects the reader can open, newest first. `revalidate` reads them again on mount. */
export function useProjects(enabled = true, { revalidate = false } = {}) {
  const { key, ready } = useScope();
  // Another account's projects never show, even for the render before the store rescopes.
  const read = () => (scope === key ? list : EMPTY_LIST);
  const state = useSyncExternalStore(subscribe, read, read);
  useEffect(() => {
    scopeProjects(key);
    if (enabled && ready && (revalidate || (!list.loaded && !list.loading))) void refreshProjects();
  }, [enabled, key, ready, revalidate]);
  return {
    projects: state.projects,
    loading: state.loading || (enabled && !state.loaded),
    loaded: state.loaded,
    error: state.error,
    refresh: refreshProjects,
  };
}

/** One project as the reader may see it. `missing` when it is gone or not theirs to open.
 * `revalidate` reads it again on mount: a project's home and its About panel show current work. */
export function useProject(id: string | null | undefined, { revalidate = false } = {}) {
  const { key, ready } = useScope();
  const read = () => (id && scope === key ? details.get(id) : undefined);
  const state = useSyncExternalStore(subscribe, read, read);
  useEffect(() => {
    scopeProjects(key);
    if (id && ready && (revalidate || !details.has(id))) void refreshProject(id);
  }, [id, key, ready, revalidate]);
  return {
    project: state?.project ?? null,
    loading: state ? state.loading : Boolean(id),
    error: state?.error ?? '',
    missing: state?.missing ?? false,
    setProject: putProject,
    refresh: () => (id ? refreshProject(id) : Promise.resolve()),
  };
}

/** What the store already knows about a project, without loading it: its name for a placeholder. */
export function useKnownProject(id: string | null | undefined): ProjectSummary | null {
  const read = () =>
    id ? (details.get(id)?.project ?? list.projects.find((p) => p.id === id) ?? null) : null;
  return useSyncExternalStore(subscribe, read, read);
}

/** Read a project again where something shows it (a chat moved in or out of it). */
export function refreshProjectIfShown(id: string | null | undefined) {
  if (id && details.has(id)) void refreshProject(id);
}

/** Whether a project turned out to be gone, or not shared with the reader. */
export function useProjectMissing(id: string | null | undefined) {
  const read = () => Boolean(id && details.get(id)?.missing);
  return useSyncExternalStore(subscribe, read, read);
}
